import React, {useCallback, useState} from 'react';
import styled from 'styled-components';
import {mobile} from 'shared/Breakpoints';
import {Player} from 'shared/components/VideoPlayer/Player';
import {Videos} from 'shared/components/VideoPlayer/Videos';
import {VideoInfo} from 'shared/components/VideoPlayer/entity/VideoInfo';
import {RoomScene} from 'shared/components/Header/RoomPlanner/NavigationGuide/RoomScene';
import {
    getCamera,
    getStepState,
} from 'shared/components/Header/RoomPlanner/NavigationGuide/scene';

interface VideoPlayerProps {
    videos: VideoInfo[];
    watched: Record<string, boolean>;
    onWatched: (video: VideoInfo) => void;
    onPlayGuide: () => void;
    onClose: () => void;
}

const REST_CAMERA = getCamera(getStepState(0));

export const VideoPlayer = ({
    videos,
    watched: initialWatched,
    onWatched,
    onPlayGuide,
    onClose,
}: VideoPlayerProps) => {
    const [videoInfo, setVideoInfo] = useState<VideoInfo>();
    const [watched, setWatched] = useState(initialWatched);

    const resetVideo = useCallback(() => {
        setVideoInfo(undefined);
    }, []);

    const handleWatched = useCallback(
        (video: VideoInfo) => {
            if (!watched[video.watchedKey]) {
                setWatched((value) => ({...value, [video.watchedKey]: true}));
                onWatched(video);
            }
        },
        [watched, onWatched]
    );

    if (videoInfo) {
        return (
            <Player
                videoInfo={videoInfo}
                videos={videos}
                watched={watched}
                resetVideo={resetVideo}
                setVideoInfo={setVideoInfo}
                onWatched={handleWatched}
                onPlayGuide={onPlayGuide}
                onClose={onClose}
            />
        );
    }

    return (
        <Help>
            <Header>
                <div>
                    <Title>3D Room Planner help</Title>
                    <Intro>
                        Everything you need to get started, in a few minutes.
                    </Intro>
                </div>
                <button
                    type="button"
                    className="btn-close"
                    aria-label="Close"
                    onClick={onClose}
                />
            </Header>
            <StartHere>
                <Illustration>
                    <RoomScene
                        camera={REST_CAMERA}
                        label="Room with an L-shaped kitchen"
                    />
                </Illustration>
                <StartHereBody>
                    <Eyebrow>Start here</Eyebrow>
                    <StartHereTitle>Find your way around</StartHereTitle>
                    <StartHereText>
                        A quick animated guide to orbiting, panning and zooming
                        around your room, with mouse or touch.
                    </StartHereText>
                    <StartHereActions>
                        <PlayGuideButton type="button" onClick={onPlayGuide}>
                            ▶ Play guide
                        </PlayGuideButton>
                        <Meta>Interactive · about 20 sec</Meta>
                    </StartHereActions>
                </StartHereBody>
            </StartHere>
            <Videos
                videos={videos}
                watched={watched}
                playVideo={setVideoInfo}
            />
        </Help>
    );
};

const Help = styled.div`
    display: flex;
    flex-direction: column;
    gap: 20px;
    padding: 8px 12px 12px;

    ${mobile`padding: 0;`}
`;

const Header = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 12px;
`;

const Title = styled.div`
    margin: 0;
    font-size: 1.6em;
    font-weight: 600;
    color: ${({theme}) => theme.colors.primary.main};

    ${mobile`font-size: 1.3em;`}
`;

const Intro = styled.p`
    margin: 4px 0 0;
    color: #60788e;
`;

const StartHere = styled.div`
    display: flex;
    align-items: stretch;
    overflow: hidden;
    border-radius: 14px;
    background: #eef4fa;

    ${mobile`flex-direction: column;`}
`;

const Illustration = styled.div`
    display: flex;
    align-items: center;
    width: 36%;
    padding: 8px;
    background: #dfeaf5;

    ${mobile`
        width: 100%;
        justify-content: center;

        > svg {
            max-height: 140px;
        }
    `}
`;

const StartHereBody = styled.div`
    flex: 1;
    padding: 20px 24px;
`;

const Eyebrow = styled.div`
    font-size: 0.75em;
    font-weight: 700;
    letter-spacing: 1.2px;
    text-transform: uppercase;
    color: #60788e;
`;

const StartHereTitle = styled.div`
    margin: 6px 0;
    font-size: 1.3em;
    font-weight: 600;
    color: ${({theme}) => theme.colors.primary.main};
`;

const StartHereText = styled.p`
    margin: 0 0 16px;
    color: #535353;
`;

const StartHereActions = styled.div`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    gap: 12px;
`;

const PlayGuideButton = styled.button`
    border: 0;
    border-radius: 9px;
    padding: 10px 18px;
    font-weight: 600;
    color: #fff;
    background: ${({theme}) => theme.colors.primary.main};
`;

const Meta = styled.span`
    font-size: 0.85em;
    color: #60788e;
`;
